/**
 * realtime-store
 *
 * Owns one private notification Realtime channel per signed-in user.
 *
 * Primary responsibilities:
 * - Share notification row events between bell and desktop bridge surfaces
 * - Share notification settings invalidations
 * - Ref-count listeners and clean up unused channels
 *
 * Dependencies:
 * - lib/realtime/private-channel-auth
 * - lib/supabase/browser-client
 */

'use client'

import type { RealtimeChannel } from '@supabase/supabase-js'

import { mapNotificationRow } from '@/lib/notifications/settings'
import {
  getRealtimeReconnectDelay,
  isRealtimeChannelFailure,
  removeRealtimeChannelOnce,
  shouldRemoveRealtimeChannel
} from '@/lib/realtime/channel-recovery'
import { syncPrivateRealtimeAuth } from '@/lib/realtime/private-channel-auth'
import { supabase } from '@/lib/supabase/browser-client'
import { safeConsole } from '@/lib/utils/safeConsole'
import type { Notification } from '@/types/notifications'

interface NotificationRealtimeListener {
  onFailure?: () => void
  onNotificationChange?: () => void
  onNotification?: (notification: Notification) => void
  onSettingsChange?: () => void
  onSubscribed?: () => void
  onUnavailable?: () => void
}

interface NotificationRealtimeSubscriber {
  id: number
  listener: NotificationRealtimeListener
}

interface NotificationRealtimeEntry {
  channel: RealtimeChannel | null
  connectVersion: number
  isSubscribed: boolean
  reconnectAttempt: number
  reconnectTimeout: ReturnType<typeof setTimeout> | null
  subscribers: Map<number, NotificationRealtimeSubscriber>
  userId: string
}

export interface NotificationRealtimeSubscription {
  unsubscribe: () => void
}

const entries = new Map<string, NotificationRealtimeEntry>()
let nextSubscriberId = 1

// --------------

function isNotificationPayloadRow(
  value: unknown
): value is Parameters<typeof mapNotificationRow>[0] {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof value.id === 'string' &&
    Array.isArray(value.channels)
  )
}

// --------------

function createEntry(userId: string): NotificationRealtimeEntry {
  const entry: NotificationRealtimeEntry = {
    channel: null,
    connectVersion: 0,
    isSubscribed: false,
    reconnectAttempt: 0,
    reconnectTimeout: null,
    subscribers: new Map(),
    userId
  }

  entries.set(userId, entry)
  return entry
}

// --------------

function getEntry(userId: string) {
  return entries.get(userId) ?? createEntry(userId)
}

// --------------

function notifyEntry(
  entry: NotificationRealtimeEntry,
  notify: (listener: NotificationRealtimeListener) => void
) {
  entry.subscribers.forEach((subscriber) => {
    notify(subscriber.listener)
  })
}

// --------------

function clearReconnectTimer(entry: NotificationRealtimeEntry) {
  if (!entry.reconnectTimeout) {
    return
  }

  clearTimeout(entry.reconnectTimeout)
  entry.reconnectTimeout = null
}

// --------------

function scheduleReconnect(entry: NotificationRealtimeEntry) {
  if (entry.reconnectTimeout || entries.get(entry.userId) !== entry) {
    return
  }

  const delayMs = getRealtimeReconnectDelay(entry.reconnectAttempt)
  entry.reconnectAttempt += 1
  entry.reconnectTimeout = setTimeout(() => {
    entry.reconnectTimeout = null
    connectEntry(entry)
  }, delayMs)
}

// --------------

async function removeEntryChannel(entry: NotificationRealtimeEntry) {
  const channel = entry.channel
  entry.channel = null
  entry.isSubscribed = false

  if (channel) {
    await removeRealtimeChannelOnce(supabase, channel)
  }
}

// --------------

function connectEntry(entry: NotificationRealtimeEntry) {
  if (entry.channel || entry.subscribers.size === 0) {
    return
  }

  entry.connectVersion += 1
  const connectVersion = entry.connectVersion
  const channel = supabase
    .channel(`notification-center:${entry.userId}`, {
      config: {
        private: true
      }
    })
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${entry.userId}`
      },
      (payload) => {
        notifyEntry(entry, (listener) => {
          listener.onNotificationChange?.()
        })

        if (!isNotificationPayloadRow(payload.new)) {
          return
        }

        const notification = mapNotificationRow(payload.new)

        notifyEntry(entry, (listener) => {
          listener.onNotification?.(notification)
        })
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notification_settings',
        filter: `user_id=eq.${entry.userId}`
      },
      () => {
        notifyEntry(entry, (listener) => {
          listener.onSettingsChange?.()
        })
      }
    )

  entry.channel = channel

  void syncPrivateRealtimeAuth(supabase)
    .then((isReady) => {
      if (entries.get(entry.userId) !== entry || connectVersion !== entry.connectVersion) {
        return
      }

      if (!isReady) {
        notifyEntry(entry, (listener) => {
          listener.onUnavailable?.()
        })
        void removeEntryChannel(entry)
        scheduleReconnect(entry)
        return
      }

      channel.subscribe((status) => {
        if (
          entries.get(entry.userId) !== entry ||
          connectVersion !== entry.connectVersion
        ) {
          return
        }

        if (status === 'SUBSCRIBED') {
          entry.isSubscribed = true
          entry.reconnectAttempt = 0
          clearReconnectTimer(entry)
          notifyEntry(entry, (listener) => {
            listener.onSubscribed?.()
          })
          return
        }

        if (isRealtimeChannelFailure(status)) {
          safeConsole.warn('Notification realtime channel failed', {
            status,
            userId: entry.userId
          })
          notifyEntry(entry, (listener) => {
            listener.onFailure?.()
          })

          if (shouldRemoveRealtimeChannel(status)) {
            void removeEntryChannel(entry)
          } else {
            entry.channel = null
            entry.isSubscribed = false
          }

          scheduleReconnect(entry)
        }
      })
    })
    .catch((error) => {
      if (entries.get(entry.userId) !== entry || connectVersion !== entry.connectVersion) {
        return
      }

      safeConsole.warn('Notification realtime auth sync failed', error)
      notifyEntry(entry, (listener) => {
        listener.onUnavailable?.()
      })
      void removeEntryChannel(entry)
      scheduleReconnect(entry)
    })
}

// --------------

function cleanupEntry(entry: NotificationRealtimeEntry) {
  clearReconnectTimer(entry)
  entry.connectVersion += 1
  entries.delete(entry.userId)
  void removeEntryChannel(entry)
}

// --------------

export function subscribeToNotificationRealtime(
  userId: string,
  listener: NotificationRealtimeListener
): NotificationRealtimeSubscription {
  const entry = getEntry(userId)
  const subscriber: NotificationRealtimeSubscriber = {
    id: nextSubscriberId,
    listener
  }
  nextSubscriberId += 1

  entry.subscribers.set(subscriber.id, subscriber)
  connectEntry(entry)

  if (entry.isSubscribed) {
    listener.onSubscribed?.()
  }

  return {
    unsubscribe: () => {
      const currentEntry = entries.get(userId)
      if (!currentEntry) {
        return
      }

      currentEntry.subscribers.delete(subscriber.id)

      if (currentEntry.subscribers.size === 0) {
        cleanupEntry(currentEntry)
      }
    }
  }
}

// --------------

export function resetNotificationRealtimeStoreForTests() {
  entries.forEach(cleanupEntry)
  entries.clear()
  nextSubscriberId = 1
}
